package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Vista solo-desarrollador: dispara CF diagnosticoSistema y renderiza estado por servicio.
 * Cada card se colorea verde/rojo según ok. Mostrar meta expandible para inspección.
 */
@Composable
fun DiagnosticoSistemaView(viewModel: AdminViewModel) {
    val resumen by viewModel.diagnostico.collectAsState()
    val cargando by viewModel.cargandoDiagnostico.collectAsState()
    val error by viewModel.errorDiagnostico.collectAsState()
    var expandidos by remember { mutableStateOf(setOf<String>()) }

    // Ejecuta al abrir la vista si aún no hay reporte.
    LaunchedEffect(Unit) {
        if (resumen == null) viewModel.ejecutarDiagnostico()
    }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
            Icon(Icons.Filled.MonitorHeart, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(28.dp))
            Spacer(modifier = Modifier.width(8.dp))
            Text("Diagnóstico Sistema", style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            Spacer(modifier = Modifier.weight(1f))
            Button(
                onClick = { viewModel.ejecutarDiagnostico() },
                enabled = !cargando,
                colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro),
                shape = RoundedCornerShape(10.dp)
            ) {
                if (cargando) {
                    CircularProgressIndicator(color = Color.White, modifier = Modifier.size(16.dp), strokeWidth = 2.dp)
                } else {
                    Icon(Icons.Filled.Refresh, contentDescription = null, tint = Color.White, modifier = Modifier.size(16.dp))
                }
                Spacer(modifier = Modifier.width(6.dp))
                Text(if (cargando) "Verificando..." else "Refrescar", color = Color.White, fontWeight = FontWeight.Bold)
            }
        }
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            "Solo desarrollador. Verifica APIs externas (SendGrid, Stripe), servicios Firebase y estado de la app. Cada check tiene timeout individual: si un servicio cuelga, no bloquea al resto.",
            color = Color.Gray, style = MaterialTheme.typography.bodySmall
        )
        Spacer(modifier = Modifier.height(12.dp))

        val r = resumen
        if (error != null) {
            Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo), shape = RoundedCornerShape(10.dp)) {
                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Error, contentDescription = null, tint = Color.Red)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(error ?: "", color = Color.Red, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                }
            }
            Spacer(modifier = Modifier.height(12.dp))
        }

        if (r == null && cargando) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = SmartVanColors.AzulOscuro)
            }
        } else if (r != null) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                ResumenChip(
                    label = "OK",
                    value = r.okTotal.toString(),
                    color = SmartVanColors.Verde,
                    modifier = Modifier.weight(1f)
                )
                ResumenChip(
                    label = "Falla",
                    value = r.failTotal.toString(),
                    color = if (r.failTotal > 0) Color.Red else Color.Gray,
                    modifier = Modifier.weight(1f)
                )
                ResumenChip(
                    label = "Total",
                    value = r.checks.size.toString(),
                    color = SmartVanColors.AzulOscuro,
                    modifier = Modifier.weight(1f)
                )
            }
            Spacer(modifier = Modifier.height(12.dp))

            LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxSize()) {
                items(r.checks, key = { it.nombre }) { check ->
                    val expandido = check.nombre in expandidos
                    DiagnosticoCheckCard(
                        check = check,
                        expandido = expandido,
                        onToggle = {
                            expandidos = if (expandido) expandidos - check.nombre else expandidos + check.nombre
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun ResumenChip(label: String, value: String, color: Color, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = color.copy(alpha = 0.12f)),
        shape = RoundedCornerShape(10.dp)
    ) {
        Column(modifier = Modifier.padding(10.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text(value, fontWeight = FontWeight.Black, color = color, style = MaterialTheme.typography.titleLarge)
            Text(label, color = color, style = MaterialTheme.typography.labelSmall)
        }
    }
}

@Composable
private fun DiagnosticoCheckCard(check: DiagnosticoCheck, expandido: Boolean, onToggle: () -> Unit) {
    val estadoColor = if (check.ok) SmartVanColors.Verde else Color.Red
    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(10.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .size(12.dp)
                        .background(estadoColor, shape = androidx.compose.foundation.shape.CircleShape)
                )
                Spacer(modifier = Modifier.width(10.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text(nombreLegible(check.nombre), fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    Text(
                        (if (check.ok) "OK" else "ERROR") + " · ${check.ms} ms",
                        color = estadoColor,
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Medium
                    )
                }
                TextButton(onClick = onToggle) {
                    Text(if (expandido) "Ocultar" else "Detalles", color = SmartVanColors.CyanAccent, style = MaterialTheme.typography.labelSmall)
                }
            }
            if (!check.ok && check.error.isNotBlank()) {
                Spacer(modifier = Modifier.height(6.dp))
                Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo), shape = RoundedCornerShape(6.dp)) {
                    Text(check.error, color = Color.Red, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(8.dp))
                }
            }
            if (expandido) {
                Spacer(modifier = Modifier.height(8.dp))
                Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoGrisClaro), shape = RoundedCornerShape(6.dp)) {
                    Text(
                        check.metaJson,
                        style = MaterialTheme.typography.bodySmall,
                        fontFamily = FontFamily.Monospace,
                        fontSize = 10.sp,
                        color = Color.DarkGray,
                        modifier = Modifier.padding(8.dp)
                    )
                }
            }
        }
    }
}

private fun nombreLegible(id: String): String = when (id) {
    "rtdb" -> "Firebase Realtime Database"
    "firebaseAuth" -> "Firebase Auth"
    "stripe" -> "Stripe (balance)"
    "sendgrid" -> "SendGrid (correos)"
    "stripeWebhookSecret" -> "Stripe Webhook Secret"
    "catalogos" -> "Catálogos (precios/rutas)"
    "logCorreosRecientes" -> "Log correos (últimas 24h)"
    "backupsRTDB" -> "Backups RTDB (Storage)"
    "reservasHuerfanas" -> "Reservas huérfanas (cs_...)"
    else -> id
}
